import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '@/containers/Administration/FieldsMaintenance/FieldsMaintenance.module.scss';
import { isObject } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import administrationServices from '@/services/administrationServices';
import { booleanCellRenderer, ReduxForms } from '@/types';
import DialogFieldMaintenance from '../DialogFieldMaintenance/DialogFieldMaintenance';
import AccordionPanel from '../UI/AccordionPanel/AccordionPanel';
import SimpleButton from '../UI/SimpleButton/SimpleButton';
import Spinner from '../UI/Spinner/Spinner';
import { ALIGN, CELL_EDITOR_TYPES, CELL_RENDERER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '../UI/TableListEditable/TableListEditableNew';

export interface FieldOption extends Record<string, unknown> {
  key: string;
  mandatory: boolean;
  active: boolean;
  DE: string;
  EN: string;
  lastBoolModified?: boolean;
}

interface StateForms {
  [formKey: string]: { options: FieldOption[] };
}

interface TabFieldsMaintenanceProps {
  tab: string;
  setStateRedux: (forms: ReduxForms) => void;
  formsTitles: { [formKey: string]: string };
  mandatoryFieldsMap?: { [formKey: string]: string[] };
}

const TabFieldsMaintenance: React.FC<TabFieldsMaintenanceProps> = (props) => {
  const { tab, setStateRedux, formsTitles, mandatoryFieldsMap = [] } = props;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [stateForms, setStateForms] = useState<StateForms>();
  const [formsKeys, setFormsKeys] = useState<string[]>([]);
  const [currentFormKey, setCurrentFormKey] = useState('');
  const [modalCreateOpen, setModalCreateOpen] = useState(false);
  const isLoading = formsKeys.some((key) => !stateForms?.[key]);

  const handleActiveCellValueChanged = (params: any, form: string) => {
    const event = { target: { checked: params.newValue } };
    const propsTable = { row: params.data, column: { name: params.colDef.field } };
    handleBooleansEdit(event, propsTable, form);
  };

  const columns = [
    { name: 'EN', title: t('administration.fieldsMaintenance.content', { lng: 'en' }), flex: 2 },
    { name: 'DE', title: t('administration.fieldsMaintenance.content', { lng: 'de' }), flex: 2},
    { name: 'active', title: t('administration.fieldsMaintenance.active'), flex:1, align: ALIGN.CENTER, cellRenderer: CELL_RENDERER_TYPES.CHECKBOX, editable: true, cellEditor: CELL_EDITOR_TYPES.CHECKBOX },
    { name: 'mandatory', title: t('mandatory'), flex: 1, align: ALIGN.CENTER, cellRenderer: booleanCellRenderer },
  ];
  const convertThisStateToReduxState = (newStateProp) => {
    // convert the options from: array to object
    let newState = {};
    formsKeys.forEach((key) => {
      newState = {
        ...newState,
        [key]: {
          options: newStateProp?.[key].options.reduce((acc, cur) => {
            acc[cur.key] = { EN: cur.EN, DE: cur.DE, active: cur.active };

            return acc;
          }, {}),
        },
        modified: true,
      };
    });

    return newState;
  };

  const editForms = (changedRows, form: string) => {
    const newState = {
      ...stateForms,
      [form]: {
        options: changedRows,
      },
    };
    setStateForms(newState);
    const stateForRedux = convertThisStateToReduxState(newState);
    setStateRedux(stateForRedux);
    // when click on save button or when the tab is changed, its saved in AdministrationHeader
  };

  const handleNewRow = (formKey: string) => {
    setCurrentFormKey(formKey);
    setModalCreateOpen(true);
  };

  const handleBooleansEdit = (event, propsTable, form: string) => {
    const checked = event?.target?.checked;
    const rowKey = propsTable?.row?.key;
    let changedRows = stateForms?.[form].options;

    if (!checked) {
      const countActiveChecks = changedRows?.reduce((acc, curr) => (curr.active ? (acc + 1) : acc), 0) ?? 0;

      if (countActiveChecks <= 1) {
        addToast(t('administration.fieldsMaintenance.errorAllDisabled'), 'ERROR');
        changedRows = changedRows?.map((current) =>
          current.key === rowKey ? { ...current, active: true } : current,
        );
        editForms(changedRows, form);

        return;
      }
    }

    editForms(changedRows, form);
  };

  const handleOnCreateNewField = (newField: FieldOption) => {
    let rows = stateForms?.[currentFormKey].options;
    rows = newField.lastBoolModified ? rows?.map((current) => ({ ...current, lastBoolModified: false })) : rows;
    rows?.push(newField);

    editForms(rows, currentFormKey);
    setModalCreateOpen(false);
  };

  const isFieldMandatory = (formKey: string, fieldKey: string) => mandatoryFieldsMap?.[formKey]?.some((fieldK) => fieldK === fieldKey);

  useEffect(() => {
    administrationServices.getFieldMaintenanceTabForms(tab)
      .then((data) => {
        setStateRedux(data?.form);
        // for each form get your key and preventing of bad options for table
        const keys = Object.keys(data?.form).filter((key) => isObject(data?.form?.[key]));
        const newKeys: string[] = [];
        // best form to hide specific forms by the formstitles object
        Object.keys(formsTitles).forEach((key) => keys.includes(key) && newKeys.push(key));

        let newState = {};
        // convert the options from: object to array
        newKeys.forEach((formKey) => {
          newState = {
            ...newState,
            [formKey]: {
              ...data?.form?.[formKey],
              options: Object.entries(data?.form?.[formKey]?.options).map(([fieldKey, object]: any[]) => (
                {
                  key: fieldKey,
                  mandatory: isFieldMandatory(formKey, fieldKey),
                  active: object?.active ?? false,
                  ...object,
                }
              )),
            },
          };
        });
        setStateForms(newState);
        setFormsKeys(newKeys);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        formsKeys.map((formKey) => (
          <AccordionPanel
            title={`${formsTitles?.[formKey]} (${stateForms?.[formKey]?.options?.length ?? 0})`}
            key={`accordion-${formKey}--${stateForms?.[formKey]?.options?.length ?? 0}`}
          >
            <div className={styles.ButtonsTableRight}>
              <SimpleButton
                id={`btn-add-new-entry-${formKey}-form`}
                color="primary"
                buttonText={t('administration.fieldsMaintenance.addRow')}
                handleClick={() => handleNewRow(formKey)}
              />
            </div>
            <div className={`${styles.ContainerTableForm} w-100`}>
              <TableListEditableNew
                list={stateForms?.[formKey]?.options ?? []}
                columns={columns}
                onCellValueChanged={(params) => handleActiveCellValueChanged(params, formKey)}
              />
            </div>
          </AccordionPanel>
        ))
      )}
      {modalCreateOpen
        && (
          <DialogFieldMaintenance
            title={t('administration.fieldsMaintenance.addRow')}
            close={() => setModalCreateOpen(false)}
            onSave={handleOnCreateNewField}
            currentFields={stateForms?.[currentFormKey]?.options ?? []}
          />
        )}
    </>
  );
};

export default (TabFieldsMaintenance);
